import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { chatEvents$ } from "../../api/webSocketRx";
import { CHUNK_SIZE } from "../../config/Constant";
import { v4 as uuidv4 } from "uuid";
import apiFactory from "../../api";
import { uploadEvent$ } from "../../api/eventBus";
import { uploadMultipartFile } from "../../api/authentication/storageApi";

export const SENDING_STATUS = {
  SENDING: "sending",
  /* dont need success */
  SUCCESS: "success",
  ERROR: "error",
  CANCEL: "cancel",
};
/*
 * fakeMessages structure:
 * 1. conversationId
 * 2. array of
 *           {
 *               message fields,
 *                requestUuid,
 *               status
 *           }

 *
 * fakeFiles structure:
 * 1. requestUuid(uid)
 *
 * 2.    {
 *          data: file,
 *          uploadingIndex,
 *
 *          status
 *       }
 * ex: fakeMessages = {
    "12345abc": [
      {
        requestUuid: "12343455",
        contentType: "text",
      },
      {
        requestUuid: "12343455",
        contentType: "text",
      },
    ],
  };
 * */

const SendingContext = createContext(null);
export const useSendingContext = () => {
  return useContext(SendingContext);
};

export const SendingProvider = ({ children }) => {
  const [fakeMessages, setFakeMessages] = useState({});
  const [fakeFiles, setFakeFiles] = useState({});

  const pushMessage = (conversationId, message) => {
    setFakeMessages((prev) => ({
      ...prev,
      [conversationId]: [...(prev?.[conversationId] || []), message],
    }));
  };

  const pushMessages = (conversationId, messageList) => {
    setFakeMessages((prev) => ({
      ...prev,
      [conversationId]: [...(prev?.[conversationId] || []), ...messageList],
    }));
  };

  const completeMessage = (conversationId, requestUuid) => {
    setFakeMessages((prev) => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).filter(
        (message) => message.requestUuid !== requestUuid,
      ),
    }));
  };

  const completeMessages = (conversationId, requestUuids) => {
    setFakeMessages((prev) => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).filter(
        (message) => !requestUuids.includes(message.requestUuid),
      ),
    }));
  };

  /* data is {
    1. file,
    2. requestUuid
    3. api controller
  }*/

  const pushFile = (data) => {
    setFakeFiles((prev) => {
      const prepareFakeFiles = { ...prev };

      prepareFakeFiles[data?.file?.uid] = {
        data: data?.file,
        status: SENDING_STATUS.SENDING,
        abortController: data?.abortController,
      };

      return prepareFakeFiles;
    });
  };
  const pushFiles = (dataList) => {
    setFakeFiles((prev) => {
      const prepareFakeFiles = { ...prev };

      dataList?.forEach((data) => {
        prepareFakeFiles[data?.file?.uid] = {
          data: data?.file,
          status: SENDING_STATUS.SENDING,
          uploadingIndex: 0,
          abortController: data?.abortController,
        };
      });

      return { ...prepareFakeFiles };
    });
  };

  const completeFile = (requestUuid) => {
    setFakeFiles((prev) => {
      const updated = { ...prev };
      updated?.[requestUuid]?.abortController?.abort();
      delete updated[requestUuid];

      return { ...updated };
    });
  };

  const completeFiles = (requestUuids) => {
    setFakeFiles((prev) => {
      const updated = { ...prev };

      requestUuids.forEach((requestUuid) => {
        updated?.[requestUuid]?.abortController?.abort();
        delete updated[requestUuid];
      });

      return updated;
    });
  };

  const uploadChunk = async (
    file,
    totalChunks,
    conversationId,
    folder,
    isThumbnail,
    previewImgResourceId,
    uploadingIndex,
    eventType,
    signal,
  ) => {
    const start = uploadingIndex * CHUNK_SIZE;
    const end = Math.min(file?.size, start + CHUNK_SIZE);

    const chunk = file.slice(start, end);

    const resourceRequest = {
      requestUuid: file?.uid,
      chunkIndex: uploadingIndex + 1,
      totalChunk: totalChunks,
      fileName: file?.name,
      folder,
      conversationId,
      actualContentType: file?.type || "application/octet-stream",
      width: file.width,
      height: file.height,
      isThumbnail,
      previewImgResourceId,
    };

    try {
      const response = await apiFactory.resourceApi.uploadChunkV2(
        chunk,
        resourceRequest,
        { signal },
      );

      if (!response?.data) {
        throw new Error(`Upload failed: ${response?.status}`);
      }

      if (eventType === "FILE") {
        updateChunk(file?.uid, conversationId);
        uploadEvent$.next({
          type: eventType,
          payload: response?.data,
        });
      }

      return response;
    } catch (error) {
      await new Promise((resolve) => setTimeout(resolve, 5000));

      return await uploadChunk(
        file,
        totalChunks,
        conversationId,
        folder,
        isThumbnail,
        previewImgResourceId,
        uploadingIndex,
        eventType,
        signal,
      );
    }
  };

  /*
   * uploadingFile structure:
   * 1. file: imageFile,
   * 2. uploadingIndex
   * 2. messageRequestUuid: preparedMessage?.requestUuid,
   * 3. abortController: new AbortController(),
   * */

  const updateChunk = (uid, conversationId) => {
    setFakeFiles((prev) => {
      if (prev?.[uid]?.uploadingIndex == null) {
        return prev;
      }

      prev[uid].uploadingIndex += 1;
      return prev;
    });

    setFakeMessages((prev) => {
      const messageIndex = prev?.[conversationId]?.findIndex(
        (conv) => conv.uid === uid,
      );

      if (prev?.[conversationId]?.[messageIndex]) {
        prev[conversationId][messageIndex].totalChunkUploaded =
          (prev[conversationId][messageIndex].totalChunkUploaded ?? 0) + 1;
      }

      return { ...prev };
    });
  };
  const uploadFileV2 = async (
    data,
    conversationId,
    folder = "FREE",
    signal,
    onProgress,
    isThumbnail = false,
    previewImgResourceId = null,
    eventType,
    message,
  ) => {
    let result;

    try {
      result = await uploadMultipartFile({
        file: data?.file,
        bucket: "conversation",
        signal,
        onProgress: ({ percent }) => {
          updateChunk(data?.file?.uid, conversationId);
          uploadEvent$.next({
            type: eventType,
            payload: {
              ...message,
              percentProgress: percent,
            },
          });
        },
      });
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") {
        return;
      }

      throw error;
    }

    if (!result || signal?.aborted) return;

    await apiFactory.messageApi.sendMessage({
      ...message,
      contentType: "FILE",
      content: result?.fileName,
      file: {
        mainPath: result?.location,

        videoImgPreviewPath: result?.videoImgPreviewPath,
        width: result?.width,
        height: result?.height,
        duration: result?.videoDuration,

        originalName: result?.fileName,
        volume: result?.fileSize,
        contentType: result?.contentType,
      },
    });
  };

  const createTextMessage = async (preparedFiles, messageRequest) => {
    let photos = [];

    const originalPreparedFiles = preparedFiles?.filter(
      (preparedFile) => !preparedFile?.originalUid,
    );
    const thumbnailPreparedFiles = preparedFiles?.filter(
      (preparedFile) => preparedFile?.originalUid,
    );

    let thumbnailMap = new Map();

    for (const preparedFile of thumbnailPreparedFiles) {
      if (!preparedFile?.file?.uid) preparedFile.file.uid = uuidv4();

      const resource = await uploadMultipartFile({
        file: preparedFile?.file,
        bucket: "conversation",
        signal: preparedFile?.abortController?.signal,
        onProgress: ({ percent }) => {},
      });

      thumbnailMap.set(preparedFile?.originalUid, resource);
    }

    for (const preparedFile of originalPreparedFiles) {
      if (!preparedFile?.file?.uid) preparedFile.file.uid = uuidv4();

      const resource = await uploadMultipartFile({
        file: preparedFile?.file,
        bucket: "conversation",
        signal: preparedFile?.abortController?.signal,
        onProgress: ({ percent }) => {},
      });

      const thumbnail = thumbnailMap.get(preparedFile?.file?.uid);

      photos.push({
        ...resource,
        originalName: resource?.fileName,
        volume: resource?.fileSize,
        contentType: "IMAGE",
        mainPath: resource?.location,
        thumbnailPreviewPath: thumbnail?.location,
        width: thumbnail?.width || resource?.width,
        height: thumbnail?.height || resource?.height,
      });
    }

    messageRequest.photos = photos;
    await apiFactory.messageApi.sendMessage(messageRequest);
  };

  const createFileMessage = async (message, resource, signal) => {
    const response = await apiFactory.messageApi.sendFile(
      {
        ...message,
        contentType: resource?.contentType,
        resource: resource,
      },
      signal,
    );

    if (!response) {
      completeFile(message?.requestUuid);
      return;
    }

    if (response?.status !== 200) {
    } else {
      completeFile(message?.requestUuid);
    }
  };

  useEffect(() => {
    const sub = chatEvents$.subscribe((eventData) => {
      if (!eventData?.requestUuid) return;

      completeMessages(eventData);
    });

    return () => sub.unsubscribe();
  }, []);

  const values = useMemo(
    () => ({
      fakeMessages,
      pushMessage,
      pushMessages,
      completeMessage,
      completeMessages,

      pushFile,
      pushFiles,
      completeFile,

      uploadFileV2,
      createTextMessage,
    }),
    [fakeMessages, fakeFiles],
  );

  return (
    <SendingContext.Provider value={values}>{children}</SendingContext.Provider>
  );
};
